import React, { createContext, useContext, useState, useCallback } from 'react';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showChatbot, setShowChatbot] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const startOnboarding = useCallback(() => {
    console.log('Starting onboarding...');
    setIsOnboarding(true);
    setCurrentStep(0);
    setShowChatbot(false);
    
    // Also set a marker to prevent multiple starts
    sessionStorage.setItem('onboarding_started', 'true');
  }, []);

  const nextStep = useCallback(() => {
    const newStep = currentStep + 1;
    console.log('nextStep called - currentStep:', currentStep, 'newStep:', newStep);
    setCurrentStep(newStep);
    
    // Show chatbot when reaching AI step (step 1)
    if (newStep === 1) {
      setShowChatbot(true);
    } else {
      setShowChatbot(false);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    const newStep = Math.max(0, currentStep - 1);
    console.log('prevStep called - currentStep:', currentStep, 'newStep:', newStep);
    setCurrentStep(newStep);
    
    // Show/hide chatbot based on step
    if (newStep === 1) {
      setShowChatbot(true);
    } else {
      setShowChatbot(false);
    }
  }, [currentStep]);

  const skipOnboarding = useCallback(() => {
    console.log('Skipping onboarding...');
    setIsOnboarding(false);
    setCurrentStep(0);
    setShowChatbot(false);
  }, []);

  const completeOnboarding = useCallback(() => {
    console.log('Completing onboarding...');
    setIsOnboarding(false);
    setCurrentStep(0);
    setShowChatbot(false);
  }, []);

  // Used by all onboarding flows to prompt before switching tours
  const requestTourSwitch = useCallback((path, message) => {
    setPendingNavigation({ path, message });
    setShowConfirmModal(true);
  }, []);

  const confirmTourSwitch = useCallback((navigate) => {
    if (pendingNavigation && navigate) {
      navigate(pendingNavigation.path);
      setShowConfirmModal(false);
      setPendingNavigation(null);
    }
  }, [pendingNavigation]);

  const cancelTourSwitch = useCallback(() => {
    setShowConfirmModal(false);
    setPendingNavigation(null);
  }, []);

  const value = {
    isOnboarding,
    currentStep,
    nextStep,
    prevStep,
    startOnboarding,
    skipOnboarding,
    completeOnboarding,
    setCurrentStep,
    showChatbot,
    setShowChatbot,
    showConfirmModal,
    pendingNavigation,
    requestTourSwitch,
    confirmTourSwitch,
    cancelTourSwitch,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
// No changes needed for step logic, steps are controlled by setCurrentStep and currentStep.
